<template>
  <div class="calendar-page">
    <!-- Page Header -->
    <div class="d-flex flex-column flex-md-row justify-space-between align-center mb-6">
      <div class="mb-4 mb-md-0">
        <h1 class="text-h4 font-weight-bold text-primary">
          Meeting Calendar
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          View and manage board meetings in calendar format
        </p>
      </div>

      <div class="d-flex align-center gap-2">
        <!-- View Toggle -->
        <v-btn-toggle
          v-model="currentView"
          color="primary"
          variant="outlined"
          divided
          mandatory
        >
          <v-btn value="month" size="small">
            <v-icon>mdi-calendar-month</v-icon>
            <span class="d-none d-md-inline ml-1">Month</span>
          </v-btn>
          <v-btn value="week" size="small">
            <v-icon>mdi-calendar-week</v-icon>
            <span class="d-none d-md-inline ml-1">Week</span>
          </v-btn>
          <v-btn value="day" size="small">
            <v-icon>mdi-calendar-today</v-icon>
            <span class="d-none d-md-inline ml-1">Day</span>
          </v-btn>
        </v-btn-toggle>

        <!-- Create Meeting Button -->
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="$router.push('/meetings/create')"
        >
          New Meeting
        </v-btn>
      </div>
    </div>

    <!-- Calendar Controls -->
    <v-card class="mb-6">
      <v-card-text class="pa-4">
        <div class="d-flex flex-column flex-md-row justify-space-between align-center">
          <!-- Navigation -->
          <div class="d-flex align-center mb-4 mb-md-0">
            <v-btn
              icon
              variant="text"
              @click="navigateCalendar(-1)"
              size="small"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div class="mx-4 text-center">
              <h2 class="text-h5 font-weight-medium">
                {{ formatCalendarTitle }}
              </h2>
              <v-btn
                variant="text"
                size="small"
                @click="goToToday"
                class="text-caption"
              >
                Today
              </v-btn>
            </div>

            <v-btn
              icon
              variant="text"
              @click="navigateCalendar(1)"
              size="small"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <!-- Filters -->
          <div class="d-flex align-center gap-2">
            <v-select
              v-model="selectedMeetingTypes"
              :items="meetingTypes"
              item-title="name"
              item-value="id"
              label="Filter by type"
              variant="outlined"
              density="compact"
              multiple
              chips
              closable-chips
              style="min-width: 200px;"
              hide-details
            />

            <v-switch
              v-model="showOnlyMyMeetings"
              label="My meetings only"
              color="primary"
              hide-details
              class="ml-2"
            />
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Calendar Grid -->
    <v-card>
      <!-- Month View -->
      <div v-if="currentView === 'month'" class="calendar-grid">
        <!-- Header with days of week -->
        <div class="calendar-header">
          <div
            v-for="day in daysOfWeek"
            :key="day"
            class="calendar-header-cell"
          >
            {{ day }}
          </div>
        </div>

        <!-- Calendar body -->
        <div class="calendar-body">
          <div
            v-for="week in calendarWeeks"
            :key="week.weekStart"
            class="calendar-week"
          >
            <div
              v-for="day in week.days"
              :key="day.date"
              class="calendar-day"
              :class="{
                'calendar-day--other-month': !day.isCurrentMonth,
                'calendar-day--today': day.isToday,
                'calendar-day--selected': day.isSelected
              }"
              @click="selectDay(day)"
            >
              <div class="calendar-day-header">
                <span class="calendar-day-number">{{ day.dayNumber }}</span>
              </div>

              <div class="calendar-day-content">
                <div
                  v-for="meeting in day.meetings"
                  :key="meeting.id"
                  class="calendar-event"
                  :class="`calendar-event--${meeting.meeting_type && typeof meeting.meeting_type === 'string' ? meeting.meeting_type.toLowerCase().replace(' ', '-') : 'default'}`"
                  @click.stop="openMeetingDetails(meeting)"
                >
                  <div class="calendar-event-time">
                    {{ formatTime(meeting.start_datetime) }}
                  </div>
                  <div class="calendar-event-title">
                    {{ meeting.title }}
                  </div>
                  <div class="calendar-event-status">
                    <v-chip
                      :color="getStatusColor(meeting.status)"
                      size="x-small"
                      variant="flat"
                    >
                      {{ meeting.status }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Week View -->
      <div v-else-if="currentView === 'week'" class="week-view">
        <div class="week-header">
          <div class="time-column"></div>
          <div
            v-for="day in weekDays"
            :key="day.date"
            class="week-day-header"
            :class="{ 'week-day-header--today': day.isToday }"
          >
            <div class="week-day-name">{{ day.dayName }}</div>
            <div class="week-day-number">{{ day.dayNumber }}</div>
          </div>
        </div>

        <div class="week-body">
          <div
            v-for="hour in hours"
            :key="hour"
            class="week-hour-row"
          >
            <div class="time-label">
              {{ formatHour(hour) }}
            </div>
            <div
              v-for="day in weekDays"
              :key="`${day.date}-${hour}`"
              class="week-time-slot"
              @click="createMeetingAtTime(day.date, hour)"
            >
              <div
                v-for="meeting in getMeetingsForDayHour(day.date, hour)"
                :key="meeting.id"
                class="week-event"
                :class="`week-event--${meeting.meeting_type && typeof meeting.meeting_type === 'string' ? meeting.meeting_type.toLowerCase().replace(' ', '-') : 'default'}`"
                @click.stop="openMeetingDetails(meeting)"
              >
                <div class="week-event-title">{{ meeting.title }}</div>
                <div class="week-event-time">
                  {{ formatTime(meeting.start_datetime) }} - 
                  {{ formatTime(meeting.end_datetime) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Day View -->
      <div v-else-if="currentView === 'day'" class="day-view">
        <div class="day-header">
          <h3 class="text-h6 font-weight-medium mb-2">
            {{ formatDayViewTitle }}
          </h3>
          <v-divider />
        </div>

        <div class="day-body">
          <div
            v-for="hour in hours"
            :key="hour"
            class="day-hour-row"
          >
            <div class="time-label">
              {{ formatHour(hour) }}
            </div>
            <div
              class="day-time-slot"
              @click="createMeetingAtTime(selectedDate, hour)"
            >
              <div
                v-for="meeting in getMeetingsForDayHour(selectedDate, hour)"
                :key="meeting.id"
                class="day-event"
                :class="`day-event--${meeting.meeting_type && typeof meeting.meeting_type === 'string' ? meeting.meeting_type.toLowerCase().replace(' ', '-') : 'default'}`"
                @click.stop="openMeetingDetails(meeting)"
              >
                <div class="day-event-title">{{ meeting.title }}</div>
                <div class="day-event-details">
                  <div class="day-event-time">
                    {{ formatTime(meeting.start_datetime) }} - 
                    {{ formatTime(meeting.end_datetime) }}
                  </div>
                  <div class="day-event-location" v-if="meeting.location">
                    <v-icon size="small">mdi-map-marker</v-icon>
                    {{ meeting.location }}
                  </div>
                </div>
                <div class="day-event-status">
                  <v-chip
                    :color="getStatusColor(meeting.status)"
                    size="small"
                    variant="flat"
                  >
                    {{ meeting.status }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Meeting Details Dialog -->
    <v-dialog
      v-model="showMeetingDialog"
      max-width="600"
      scrollable
    >
      <v-card v-if="selectedMeeting">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ selectedMeeting.title }}</span>
          <v-btn
            icon
            variant="text"
            @click="showMeetingDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="meeting-details">
            <div class="detail-row">
              <v-icon class="mr-2">mdi-calendar</v-icon>
              <span>{{ formatMeetingDate(selectedMeeting.start_datetime) }}</span>
            </div>

            <div class="detail-row">
              <v-icon class="mr-2">mdi-clock</v-icon>
              <span>
                {{ formatTime(selectedMeeting.start_datetime) }}
                <span v-if="selectedMeeting.end_datetime">
                  - {{ formatTime(selectedMeeting.end_datetime) }}
                </span>
              </span>
            </div>

            <div class="detail-row" v-if="selectedMeeting.location">
              <v-icon class="mr-2">mdi-map-marker</v-icon>
              <span>{{ selectedMeeting.location }}</span>
            </div>

            <div class="detail-row">
              <v-icon class="mr-2">mdi-account-group</v-icon>
              <span>{{ selectedMeeting.attendees?.length || 0 }} attendees</span>
            </div>

            <div class="detail-row">
              <v-icon class="mr-2">mdi-flag</v-icon>
              <v-chip
                :color="getStatusColor(selectedMeeting.status)"
                size="small"
                variant="flat"
              >
                {{ selectedMeeting.status }}
              </v-chip>
            </div>

            <div class="detail-row" v-if="selectedMeeting.description">
              <v-icon class="mr-2">mdi-text</v-icon>
              <span>{{ selectedMeeting.description }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="outlined"
            @click="editMeeting(selectedMeeting)"
          >
            Edit Meeting
          </v-btn>
          <v-btn
            color="primary"
            @click="viewMeetingDetails(selectedMeeting)"
          >
            View Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'Calendar',
  setup() {
    const router = useRouter()
    const toast = useToast()

    // State
    const currentView = ref('month')
    const selectedDate = ref(new Date())
    const meetings = ref([])
    const loading = ref(false)
    const showMeetingDialog = ref(false)
    const selectedMeeting = ref(null)
    const selectedMeetingTypes = ref([])
    const showOnlyMyMeetings = ref(false)

    // Reference data
    const meetingTypes = ref([
      { id: 1, name: 'Regular Board Meeting', color: '#1565C0' },
      { id: 2, name: 'Emergency Meeting', color: '#F44336' },
      { id: 3, name: 'Committee Meeting', color: '#FF9800' },
      { id: 4, name: 'Strategic Planning', color: '#4CAF50' }
    ])

    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const hours = Array.from({ length: 24 }, (_, i) => i)

    // Computed properties
    const formatCalendarTitle = computed(() => {
      const date = selectedDate.value
      const options = { 
        year: 'numeric', 
        month: 'long',
        ...(currentView.value === 'day' && { day: 'numeric' })
      }
      return date.toLocaleDateString('en-US', options)
    })

    const formatDayViewTitle = computed(() => {
      const date = selectedDate.value
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    })

    const filteredMeetings = computed(() => {
      let filtered = meetings.value

      if (selectedMeetingTypes.value.length > 0) {
        filtered = filtered.filter(meeting => 
          selectedMeetingTypes.value.includes(meeting.meeting_type_id)
        )
      }

      if (showOnlyMyMeetings.value) {
        // Filter for current user's meetings
        filtered = filtered.filter(meeting => 
          meeting.attendees?.some(attendee => attendee.id === 1) // Replace with actual user ID
        )
      }

      return filtered
    })

    const calendarWeeks = computed(() => {
      const date = new Date(selectedDate.value)
      const year = date.getFullYear()
      const month = date.getMonth()
      
      const firstDayOfMonth = new Date(year, month, 1)
      const lastDayOfMonth = new Date(year, month + 1, 0)
      
      const startDate = new Date(firstDayOfMonth)
      startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay())
      
      const endDate = new Date(lastDayOfMonth)
      endDate.setDate(endDate.getDate() + (6 - lastDayOfMonth.getDay()))
      
      const weeks = []
      const currentDate = new Date(startDate)
      
      while (currentDate <= endDate) {
        const week = {
          weekStart: new Date(currentDate),
          days: []
        }
        
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(currentDate)
          const dayMeetings = filteredMeetings.value.filter(meeting => {
            const meetingDate = new Date(meeting.start_datetime)
            return meetingDate.toDateString() === dayDate.toDateString()
          })
          
          week.days.push({
            date: dayDate.toISOString().split('T')[0],
            dayNumber: dayDate.getDate(),
            isCurrentMonth: dayDate.getMonth() === month,
            isToday: dayDate.toDateString() === new Date().toDateString(),
            isSelected: dayDate.toDateString() === selectedDate.value.toDateString(),
            meetings: dayMeetings
          })
          
          currentDate.setDate(currentDate.getDate() + 1)
        }
        
        weeks.push(week)
      }
      
      return weeks
    })

    const weekDays = computed(() => {
      const startOfWeek = getStartOfWeek(selectedDate.value)
      const days = []
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek)
        date.setDate(date.getDate() + i)
        
        days.push({
          date: date.toISOString().split('T')[0],
          dayName: daysOfWeek[date.getDay()],
          dayNumber: date.getDate(),
          isToday: date.toDateString() === new Date().toDateString()
        })
      }
      
      return days
    })

    // Methods
    const getStartOfWeek = (date) => {
      const start = new Date(date)
      start.setDate(start.getDate() - start.getDay())
      return start
    }

    const navigateCalendar = (direction) => {
      const date = new Date(selectedDate.value)
      
      if (currentView.value === 'month') {
        date.setMonth(date.getMonth() + direction)
      } else if (currentView.value === 'week') {
        date.setDate(date.getDate() + (direction * 7))
      } else if (currentView.value === 'day') {
        date.setDate(date.getDate() + direction)
      }
      
      selectedDate.value = date
    }

    const goToToday = () => {
      selectedDate.value = new Date()
    }

    const selectDay = (day) => {
      selectedDate.value = new Date(day.date)
      if (currentView.value === 'month') {
        currentView.value = 'day'
      }
    }

    const formatTime = (dateTime) => {
      if (!dateTime) return 'Not set'
      
      const date = new Date(dateTime)
      if (isNaN(date.getTime())) return 'Invalid Time'
      
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }

    const formatHour = (hour) => {
      const date = new Date()
      date.setHours(hour, 0, 0, 0)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true
      })
    }

    const formatMeetingDate = (dateTime) => {
      if (!dateTime) return 'Date not set'
      
      const date = new Date(dateTime)
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const getStatusColor = (status) => {
      const colors = {
        'scheduled': 'primary',
        'in-progress': 'warning',
        'completed': 'success',
        'cancelled': 'error',
        'draft': 'grey'
      }
      return colors[status] || 'grey'
    }

    const getMeetingsForDayHour = (date, hour) => {
      return filteredMeetings.value.filter(meeting => {
        const meetingDate = new Date(meeting.start_datetime)
        return meetingDate.toDateString() === new Date(date).toDateString() &&
               meetingDate.getHours() === hour
      })
    }

    const openMeetingDetails = (meeting) => {
      selectedMeeting.value = meeting
      showMeetingDialog.value = true
    }

    const viewMeetingDetails = (meeting) => {
      router.push(`/meetings/${meeting.id}`)
    }

    const editMeeting = (meeting) => {
      router.push(`/meetings/${meeting.id}/edit`)
    }

    const createMeetingAtTime = (date, hour) => {
      const meetingDate = new Date(date)
      meetingDate.setHours(hour, 0, 0, 0)
      
      router.push({
        path: '/meetings/create',
        query: {
          date: meetingDate.toISOString().split('T')[0],
          time: meetingDate.toTimeString().split(' ')[0].substring(0, 5)
        }
      })
    }

    const loadMeetings = async () => {
      loading.value = true
      try {
        const response = await api.get('/meetings/')
        meetings.value = response.data.meetings || []
      } catch (error) {
        console.error('Error loading meetings:', error)
        toast.error('Failed to load meetings')
      } finally {
        loading.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadMeetings()
    })

    // Watch for view changes to reload data if needed
    watch(currentView, () => {
      // Could implement view-specific data loading here
    })

    return {
      // State
      currentView,
      selectedDate,
      meetings,
      loading,
      showMeetingDialog,
      selectedMeeting,
      selectedMeetingTypes,
      showOnlyMyMeetings,

      // Reference data
      meetingTypes,
      daysOfWeek,
      hours,

      // Computed
      formatCalendarTitle,
      formatDayViewTitle,
      filteredMeetings,
      calendarWeeks,
      weekDays,

      // Methods
      navigateCalendar,
      goToToday,
      selectDay,
      formatTime,
      formatHour,
      formatMeetingDate,
      getStatusColor,
      getMeetingsForDayHour,
      openMeetingDetails,
      viewMeetingDetails,
      editMeeting,
      createMeetingAtTime,
      loadMeetings
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.calendar-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  // Month View Styles
  .calendar-grid {
    .calendar-header {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);

      .calendar-header-cell {
        padding: 16px 8px;
        text-align: center;
        font-weight: 600;
        background-color: rgb(var(--v-theme-surface-variant));
        border-right: 1px solid rgba(0, 0, 0, 0.12);

        &:last-child {
          border-right: none;
        }
      }
    }

    .calendar-body {
      .calendar-week {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);

        &:last-child {
          border-bottom: none;
        }
      }

      .calendar-day {
        min-height: 120px;
        border-right: 1px solid rgba(0, 0, 0, 0.12);
        cursor: pointer;
        transition: background-color 0.2s;

        &:last-child {
          border-right: none;
        }

        &:hover {
          background-color: rgba(var(--v-theme-primary), 0.04);
        }

        &.calendar-day--other-month {
          color: rgba(0, 0, 0, 0.4);
          background-color: rgba(0, 0, 0, 0.02);
        }

        &.calendar-day--today {
          background-color: rgba(var(--v-theme-primary), 0.08);
        }

        &.calendar-day--selected {
          background-color: rgba(var(--v-theme-primary), 0.12);
        }

        .calendar-day-header {
          padding: 8px;
          text-align: center;

          .calendar-day-number {
            font-weight: 600;
          }
        }

        .calendar-day-content {
          padding: 0 4px 4px;
          max-height: 80px;
          overflow-y: auto;

          .calendar-event {
            margin-bottom: 2px;
            padding: 2px 4px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.75rem;
            line-height: 1.2;
            background-color: rgba(var(--v-theme-primary), 0.1);
            border-left: 3px solid rgb(var(--v-theme-primary));

            &:hover {
              background-color: rgba(var(--v-theme-primary), 0.2);
            }

            .calendar-event-time {
              font-weight: 600;
            }

            .calendar-event-title {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .calendar-event-status {
              margin-top: 2px;
            }
          }
        }
      }
    }
  }

  // Week View Styles
  .week-view {
    .week-header {
      display: grid;
      grid-template-columns: 60px repeat(7, 1fr);
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);

      .time-column {
        background-color: rgb(var(--v-theme-surface-variant));
      }

      .week-day-header {
        padding: 16px 8px;
        text-align: center;
        border-right: 1px solid rgba(0, 0, 0, 0.12);
        background-color: rgb(var(--v-theme-surface-variant));

        &.week-day-header--today {
          background-color: rgba(var(--v-theme-primary), 0.1);
          color: rgb(var(--v-theme-primary));
        }

        .week-day-name {
          font-weight: 600;
          font-size: 0.875rem;
        }

        .week-day-number {
          font-size: 1.25rem;
          font-weight: 700;
        }
      }
    }

    .week-body {
      .week-hour-row {
        display: grid;
        grid-template-columns: 60px repeat(7, 1fr);
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        min-height: 60px;

        .time-label {
          padding: 8px;
          text-align: center;
          font-size: 0.75rem;
          background-color: rgb(var(--v-theme-surface-variant));
          border-right: 1px solid rgba(0, 0, 0, 0.12);
        }

        .week-time-slot {
          border-right: 1px solid rgba(0, 0, 0, 0.06);
          cursor: pointer;
          position: relative;

          &:hover {
            background-color: rgba(var(--v-theme-primary), 0.04);
          }

          .week-event {
            position: absolute;
            top: 2px;
            left: 2px;
            right: 2px;
            padding: 4px;
            border-radius: 4px;
            background-color: rgba(var(--v-theme-primary), 0.1);
            border-left: 3px solid rgb(var(--v-theme-primary));
            cursor: pointer;
            font-size: 0.75rem;

            &:hover {
              background-color: rgba(var(--v-theme-primary), 0.2);
            }

            .week-event-title {
              font-weight: 600;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .week-event-time {
              font-size: 0.7rem;
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  // Day View Styles
  .day-view {
    .day-header {
      padding: 16px;
      background-color: rgb(var(--v-theme-surface-variant));
    }

    .day-body {
      .day-hour-row {
        display: grid;
        grid-template-columns: 80px 1fr;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        min-height: 80px;

        .time-label {
          padding: 16px 8px;
          text-align: center;
          background-color: rgb(var(--v-theme-surface-variant));
          border-right: 1px solid rgba(0, 0, 0, 0.12);
        }

        .day-time-slot {
          padding: 8px;
          cursor: pointer;
          position: relative;

          &:hover {
            background-color: rgba(var(--v-theme-primary), 0.04);
          }

          .day-event {
            margin-bottom: 8px;
            padding: 8px;
            border-radius: 8px;
            background-color: rgba(var(--v-theme-primary), 0.1);
            border-left: 4px solid rgb(var(--v-theme-primary));
            cursor: pointer;

            &:hover {
              background-color: rgba(var(--v-theme-primary), 0.2);
            }

            .day-event-title {
              font-weight: 600;
              margin-bottom: 4px;
            }

            .day-event-details {
              font-size: 0.875rem;
              margin-bottom: 4px;

              .day-event-time {
                margin-bottom: 2px;
              }

              .day-event-location {
                display: flex;
                align-items: center;
                gap: 4px;
                color: rgba(0, 0, 0, 0.6);
              }
            }
          }
        }
      }
    }
  }

  // Meeting Details Dialog
  .meeting-details {
    .detail-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      font-size: 0.875rem;
    }
  }
}
</style>